'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { type ReactNode, Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { gtmDatasets } from '@/utils/analytics/gtm';
import { resolveCouponProducts } from '../_utils/resolveCouponProducts';
import { BenefitHomeSectionHeader } from './BenefitHomeSectionHeader';
import { BENEFIT_SECTION_CLASS_NAME } from './BenefitMenuSection';

//* MO 3열×3줄=9개, PC 4열×2줄=8개 — 뷰포트 분기는 CSS로 하므로 9개를 받고 PC에서 마지막 1개를 숨긴다.
const COUPON_PRODUCT_LIMIT = 9;
const GRID_CLASS_NAME = 'grid grid-cols-3 gap-12 xl:grid-cols-4';
const useCouponBrands = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...UnauthQueryOptions["get:/wello/v2/allows/point-market/product/by-brand"](),
      select: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useSuspenseQuery(t0);
};
type CouponProduct = ReturnType<typeof useCouponBrands>['data'][number]['products'][number] & {
  brandName: string;
};
const SectionHeading = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <BenefitHomeSectionHeader icon={<PointCoin currency="WELLO_POINT" size={24} />} title={"\uCFE0\uD3F0"} moreHref={ROUTES.POINT_MARKET.pathname} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col gap-4">{t0}<p className="font-500 text-Gray-550 typo-body4"><span className="text-Y-600">웰포인트</span>로 만나는 쿠폰</p></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface BrandChipProps {
  label: string;
  isSelected: boolean;
  onSelect: () => void;
  children: ReactNode;
}
const BrandChip = t0 => {
  const $ = _c(13);
  const {
    label,
    isSelected,
    onSelect,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("group -m-4 flex w-62 shrink-0 flex-col items-center gap-4 rounded-xsmall p-4", FOCUS_RING);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <span className="relative flex size-54 items-center justify-center overflow-hidden rounded-large border border-Gray-300 bg-Gray-50 transition-colors duration-150 group-hover:border-Gray-400 group-focus-visible:border-Gray-400 group-active:border-Gray-400 motion-reduce:transition-none">{children}</span>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isSelected ? "font-600" : "font-400";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("w-full truncate text-center text-Gray-700 typo-caption1", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== label || $[6] !== t4) {
    t5 = <span className={t4}>{label}</span>;
    $[5] = label;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isSelected || $[9] !== onSelect || $[10] !== t2 || $[11] !== t5) {
    t6 = <button aria-pressed={isSelected} onClick={onSelect} className={t1}>{t2}{t5}</button>;
    $[8] = isSelected;
    $[9] = onSelect;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const CouponProductCard = t0 => {
  const $ = _c(32);
  const {
    product
  } = t0;
  let t1;
  if ($[0] !== product.productIdIdx) {
    t1 = ROUTES.POINT_MARKET_DETAIL.pathname({
      idx: product.productIdIdx
    });
    $[0] = product.productIdIdx;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("group flex flex-col gap-8 rounded-xlarge", FOCUS_RING);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== product.brandName || $[4] !== product.originalPrice || $[5] !== product.productIdIdx || $[6] !== product.productName) {
    t3 = gtmDatasets.selectPoint({
      product_name: product.productName,
      product_brand: product.brandName,
      product_value: product.originalPrice,
      product_id: product.productIdIdx
    });
    $[3] = product.brandName;
    $[4] = product.originalPrice;
    $[5] = product.productIdIdx;
    $[6] = product.productName;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== product.thumbnailUrl) {
    t4 = product.thumbnailUrl ? <Image src={product.thumbnailUrl} alt="" fill={true} sizes="(min-width: 1280px) 120px, 33vw" className="object-cover" /> : null;
    $[8] = product.thumbnailUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <span className="relative aspect-square w-full overflow-hidden rounded-xlarge border border-Gray-black/5 bg-Gray-100 transition-opacity duration-150 group-hover:opacity-80 group-active:opacity-80 motion-reduce:transition-none">{t4}</span>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== product.brandName) {
    t6 = <span className="truncate font-600 text-Gray-600 typo-caption2">{product.brandName}</span>;
    $[12] = product.brandName;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== product.productName) {
    t7 = <span className="line-clamp-2 font-600 text-Gray-800 typo-caption1">{product.productName}</span>;
    $[14] = product.productName;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== product.originalPrice) {
    t8 = product.originalPrice.toLocaleString();
    $[16] = product.originalPrice;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <span className="typo-body4">{t8}</span>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="typo-caption1">원</span>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t9) {
    t11 = <span className="font-600 text-Gray-black">{t9}{t10}</span>;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t6 || $[25] !== t7) {
    t12 = <span className="flex flex-col gap-2">{t6}{t7}{t11}</span>;
    $[23] = t11;
    $[24] = t6;
    $[25] = t7;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t1 || $[28] !== t12 || $[29] !== t3 || $[30] !== t5) {
    t13 = <CustomLink href={t1} className={t2} {...t3}>{t5}{t12}</CustomLink>;
    $[27] = t1;
    $[28] = t12;
    $[29] = t3;
    $[30] = t5;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
const CouponBrandBoard = () => {
  const $ = _c(8);
  const {
    data: brands
  } = useCouponBrands();
  const [selectedBrandName, setSelectedBrandName] = useState(null);
  let t0;
  if ($[0] !== brands || $[1] !== selectedBrandName) {
    const products = resolveCouponProducts(brands, selectedBrandName, COUPON_PRODUCT_LIMIT);
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SectionHeading />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== selectedBrandName) {
      t2 = t3 => {
        const {
          brandName,
          brandImageUrl
        } = t3;
        return <BrandChip key={brandName} label={brandName} isSelected={selectedBrandName === brandName} onSelect={() => setSelectedBrandName(previous => previous === brandName ? null : brandName)}>{brandImageUrl ? <Image src={brandImageUrl} alt="" fill={true} sizes="54px" className="bg-Gray-white object-contain p-4" /> : null}</BrandChip>;
      };
      $[4] = selectedBrandName;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const t3 = brands.map(t2);
    let t4;
    if ($[6] !== t3) {
      t4 = <div className="-mx-20 -mt-4 mb-4 flex gap-12 overflow-x-auto px-20 py-4 hide-scrollbar webkit-scroll-touch">{t3}</div>;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t0 = <section className={BENEFIT_SECTION_CLASS_NAME}>{t1}{t4}{products.length === 0 ? <p className="flex min-h-120 items-center justify-center rounded-medium bg-Gray-50 font-500 text-Gray-500 typo-body4">쿠폰을 준비하고 있어요.</p> : <ul className={GRID_CLASS_NAME}>{products.map(_temp2)}</ul>}</section>;
    $[0] = brands;
    $[1] = selectedBrandName;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const CouponBrandBoardFallback = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionHeading />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <section className={BENEFIT_SECTION_CLASS_NAME}>{t0}<ul aria-hidden={true} className={GRID_CLASS_NAME}>{Array.from({
          length: COUPON_PRODUCT_LIMIT
        }, _temp3)}</ul></section>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const BenefitCouponSection = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ErrorBoundary fallback={null}><Suspense fallback={<CouponBrandBoardFallback />}><CouponBrandBoard /></Suspense></ErrorBoundary>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(response) {
  return response.context?.contents ?? [];
}
function _temp2(product, index) {
  return <li key={product.productIdIdx} className={cn("flex flex-col", index === COUPON_PRODUCT_LIMIT - 1 && "xl:hidden")}><CouponProductCard product={product} /></li>;
}
function _temp3(_, index) {
  return <li key={index} className={cn("flex flex-col gap-8", index === COUPON_PRODUCT_LIMIT - 1 && "xl:hidden")}><Skeleton className="aspect-square w-full rounded-xlarge" /><Skeleton typo="caption1" className="w-full rounded-xxsmall" /><Skeleton typo="body4" className="w-2/3 rounded-xxsmall" /></li>;
}